import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type ToastType = 'error' | 'success' | 'info';

type Toast = {
  id: string;
  message: string;
  type: ToastType;
};

type ToastCtx = {
  toast: (message: string, type?: ToastType) => void;
};

const ToastContext = createContext<ToastCtx>({ toast: () => {} });

export const useToast = () => useContext(ToastContext);

const TOAST_DURATION = 4000;

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const [exiting, setExiting] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setExiting(true);
      setTimeout(onRemove, 200);
    }, TOAST_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [onRemove]);

  const dismiss = () => {
    clearTimeout(timerRef.current);
    setExiting(true);
    setTimeout(onRemove, 200);
  };

  const colors: Record<ToastType, string> = {
    error: 'border-red-500/40 bg-red-950/80 text-red-200',
    success: 'border-gold/40 bg-dark-50/90 text-gold-200',
    info: 'border-sage-600/40 bg-dark-50/90 text-sage-200',
  };

  const icons: Record<ToastType, string> = {
    error: '✕',
    success: '✓',
    info: 'ℹ',
  };

  return (
    <div
      role="alert"
      onClick={dismiss}
      className={`
        flex items-start gap-2.5 px-4 py-3 rounded-lg border backdrop-blur-sm
        cursor-pointer shadow-lg max-w-sm w-full
        transition-all duration-200 ease-out
        ${colors[toast.type]}
        ${exiting ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}
      `}
    >
      <span className="text-sm font-bold shrink-0 mt-0.5">{icons[toast.type]}</span>
      <p className="text-sm leading-snug">{toast.message}</p>
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: ToastType = 'error') => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev.slice(-4), { id, message, type }]); // keep max 5
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast: addToast }}>
      {children}
      {createPortal(
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2 items-center pointer-events-none">
          {toasts.map((t) => (
            <div key={t.id} className="pointer-events-auto">
              <ToastItem toast={t} onRemove={() => removeToast(t.id)} />
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}
